import React from "react";
import styled, { css } from "styled-components";
import { white } from "../constants/styles";
import { apiGet } from "../api";

export const LandingSearchContext = React.createContext([]);

const UnnamedComponent = ({ children }) => {
  const [searchBoxShown, setSearchBoxShown] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [isRequesting, setIsRequesting] = React.useState(false);
  const [showTyping, setShowTyping] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const inputRef = React.useRef();
  const timeoutRef = React.useRef();
  const handleInputChange = React.useCallback(
    (evt) => {
      const value = evt.target.value;
      if (isRequesting) {
        evt.preventDefault();
        return;
      }

      setInputValue(value);
      if (!value) {
        setShowTyping(false);
        setItems([]);
        return;
      } else if (value.length < 3) {
        setShowTyping(true);
        setItems([]);
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(async () => {
        setIsRequesting(true);
        setShowTyping(false);
        const { parsedResponse } = await apiGet(`/api/v1/search/${value}`);
        setItems(parsedResponse);
        setIsRequesting(false);
      }, 700);
    },
    [isRequesting, setIsRequesting, setInputValue, setShowTyping, setItems],
  );

  const toggleSearchBox = React.useCallback(() => {
    if (searchBoxShown) {
      setSearchBoxShown(false);
    } else {
      setSearchBoxShown(true);
    }
  }, [searchBoxShown]);
  const handleEscapeClick = React.useCallback(
    (evt) => {
      if (evt.key === "Escape") {
        setSearchBoxShown(false);
      }
    },
    [setSearchBoxShown],
  );
  React.useEffect(() => {
    if (searchBoxShown) {
      document.addEventListener("keydown", handleEscapeClick);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      document.removeEventListener("keydown", handleEscapeClick);
    }
    return () => {
      document.removeEventListener("keydown", handleEscapeClick);
    };
  }, [searchBoxShown, handleEscapeClick]);

  const searchBox = (
    <SearchBoxContainer $shown={searchBoxShown}>
      <SearchBoxOverlay />
      <SearchBox>
        <SearchBoxCloseIcon
          className="fas fa-times-circle"
          onClick={toggleSearchBox}
        />
        <SearchInput
          placeholder="Cari..."
          value={inputValue}
          onChange={handleInputChange}
          {...(searchBoxShown && {
            autoFocus: true,
          })}
          ref={inputRef}
        />
        {showTyping ? (
          <TypingNotice>Typing...</TypingNotice>
        ) : isRequesting ? (
          <TypingNotice>Loading...</TypingNotice>
        ) : (
          inputValue &&
          items.length == 0 && (
            <TypingNotice>No result found. Try different keyword!</TypingNotice>
          )
        )}
        <SearchResultContainer>
          {items.map(({ id, ...item }) => (
            <SearchItem key={id}>
              <SearchItemImg src={item.img} loading="lazy" />
              <SearchItemRightDiv>
                <SearchItemTitle>{item.title}</SearchItemTitle>
                <SearchItemTags>
                  {item.tags.map((tag, idx) => (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <SearchItemTag key={idx}>
                      <b>#</b>
                      {tag.name}
                    </SearchItemTag>
                  ))}
                </SearchItemTags>
              </SearchItemRightDiv>
            </SearchItem>
          ))}
        </SearchResultContainer>
      </SearchBox>
    </SearchBoxContainer>
  );

  const passed = React.useMemo(
    () => ({ toggleSearchBox, searchBox }),
    [toggleSearchBox, searchBox],
  );
  return <LandingSearchContext value={passed}>{children}</LandingSearchContext>;
};
export default UnnamedComponent;

const SearchBoxContainer = styled.div`
  display: none;
  height: 100vh;
  position: fixed;
  width: 100%;
  z-index: 1;

  ${(props) =>
    props.$shown &&
    css`
      display: block;
    `}
`;

const SearchBoxOverlay = styled.div`
  backdrop-filter: blur(3px);
  background-color: #0000007a;
  height: 100vh;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const SearchBox = styled.div`
  background-color: #3b3b3b;
  border-radius: 10px;
  left: 50%;
  max-width: 80%;
  padding: 25px 12px 15px 12px;
  position: absolute;
  top: 25%;
  transform: translate(-50%, -25%);
  width: 650px;
`;

const SearchBoxCloseIcon = styled.i`
  color: #8a8a8a;
  cursor: pointer;
  font-size: 23px;
  position: absolute;
  right: -6px;
  top: -10px;
`;

const SearchInput = styled.input`
  background-color: #2f2f2f;
  border-radius: 0.375rem;
  border: none;
  color: ${white};
  line-height: 1.5;
  outline: none;
  padding: 0.375rem 0.375rem 0.375rem 2.5rem;
  width: -webkit-fill-available;
`;

const TypingNotice = styled.div`
  color: ${white};
  margin-left: 5px;
  margin-top: 10px;
`;

const SearchResultContainer = styled.div`
  color: #fff;
  margin: 11px 9px 4px 10px;
  max-height: 70vh;
  overflow-y: scroll;
`;

const SearchItem = styled.div`
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  margin-top: 10px;
  padding: 12px;

  &:hover {
    background-color: #00000040;
  }
`;

const SearchItemImg = styled.img`
  height: 40px;
  object-fit: cover;
  min-width: 40px;
  width: 40px;
`;

const SearchItemRightDiv = styled.div`
  margin-left: 12px;
`;

const SearchItemTitle = styled.div`
  font-weight: bold;
`;

const SearchItemTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 2px;
`;

const SearchItemTag = styled.span`
  background-color: #5b5b5b;
  border-radius: 2px;
  color: #ffffff;
  font-size: 13px;
  padding: 1px 3px;
`;
